import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatIsoDateForDisplay } from '../DateField/DateField';
import type { AbsenceApptConflictDto, AbsencePolicyBreachDto } from '../../types/api-responses';
import s from './AbsenceConflictConfirm.module.css';

/**
 * AB#2016 + AB#2024 — warn-and-confirm dialog. Shown when the backend reports
 * that saving the absence needs owner confirmation: the staff has live customer
 * bookings inside the window (they'd be orphaned) and/or the break exceeds the
 * branch's per-day caps. The owner can save anyway or cancel.
 */
export interface AbsenceConflictConfirmProps {
    conflicts: AbsenceApptConflictDto[];
    /** AB#2024 — set when the break exceeds the branch caps (override allowed). */
    policyBreach?: AbsencePolicyBreachDto | null;
    saving: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

const AbsenceConflictConfirm: React.FC<AbsenceConflictConfirmProps> = ({
    conflicts, policyBreach, saving, onConfirm, onCancel,
}) => {
    const { t, language } = useLanguage();
    const shown = conflicts.slice(0, 6);
    const hasConflicts = conflicts.length > 0;
    const title = hasConflicts
        ? t('staff.absence.conflict.title')
        : t('staff.absence.break.overLimit.title');
    return (
        <div className={s.overlay} onMouseDown={onCancel}>
            <div
                className={s.card}
                onMouseDown={(e) => e.stopPropagation()}
                role="alertdialog"
                aria-modal="true"
            >
                <h3 className={s.title}>{title}</h3>

                {hasConflicts && (
                    <>
                        <p className={s.body}>
                            {t('staff.absence.conflict.body').replace('{count}', String(conflicts.length))}
                        </p>
                        <ul className={s.list}>
                            {shown.map((c) => (
                                <li key={c.appointmentId} className={s.item}>
                                    <span className={s.when}>{formatIsoDateForDisplay(String(c.date ?? '').slice(0, 10), language) || c.date} ⁨{String(c.startTime ?? '').slice(0, 5)}–{String(c.endTime ?? '').slice(0, 5)}⁩</span>
                                    <span className={s.svc}>
                                        {c.serviceName}{c.customerName ? ` · ${c.customerName}` : ''}
                                    </span>
                                </li>
                            ))}
                            {conflicts.length > shown.length && (
                                <li className={s.more}>
                                    {t('staff.absence.conflict.more').replace(
                                        '{count}', String(conflicts.length - shown.length))}
                                </li>
                            )}
                        </ul>
                    </>
                )}

                {policyBreach && (
                    <p className={s.body}>
                        {t('staff.absence.break.overLimit.body')
                            .replace('{maxBreaks}', String(policyBreach.maxBreaksPerDay))
                            .replace('{maxMin}', String(policyBreach.maxMinutesPerDay))
                            .replace('{used}', String(policyBreach.minutesToday))}
                    </p>
                )}

                <div className={s.actions}>
                    <button type="button" className={s.saveBtn} disabled={saving} onClick={onConfirm}>
                        {t('staff.absence.conflict.saveAnyway')}
                    </button>
                    <button type="button" className={s.cancelBtn} onClick={onCancel}>
                        {t('staff.absence.conflict.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AbsenceConflictConfirm;
